import { Link, router } from "@inertiajs/react"
import { Shield } from "lucide-react"
import { useState } from "react"

import { PlayerStatusBadge } from "@/components/player-status-badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { cn } from "@/lib/utils"
import { leagueTeamPlayerPath, setLineupLeagueMatchPath } from "@/routes"
import type { MatchRosterPlayer } from "@/types"

export function LineupSelector({
  leagueId,
  matchId,
  userTeamId,
  userRoster,
  currentLineupIds,
}: {
  leagueId: string
  matchId: string
  userTeamId: string
  userRoster: MatchRosterPlayer[]
  currentLineupIds: string[]
}) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(currentLineupIds),
  )

  const activeCount = userRoster.filter((p) => p.status === "active").length
  const selectedCount = selectedIds.size
  const unavailableCount = userRoster.filter(
    (p) => p.status !== "active",
  ).length
  const missNextCount = userRoster.filter(
    (p) => p.status === "miss_next",
  ).length
  const deadCount = userRoster.filter((p) => p.status === "dead").length
  const shortfall = Math.max(0, 11 - activeCount)

  function togglePlayer(playerId: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(playerId)) {
        next.delete(playerId)
      } else {
        if (next.size >= 11) return prev
        next.add(playerId)
      }
      return next
    })
  }

  function handleConfirm() {
    void router.patch(setLineupLeagueMatchPath(leagueId, matchId), {
      player_ids: [...selectedIds],
    })
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Shield className="size-4" />
            Set Your Lineup
          </CardTitle>
          <span
            className={cn(
              "text-sm font-semibold",
              selectedCount === 11
                ? "text-[var(--neon-green)]"
                : "text-muted-foreground",
            )}
          >
            {selectedCount}/11 selected
          </span>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {shortfall > 0 && (
          <div className="border-b px-4 py-2">
            <p className="text-sm text-[var(--accent-dim)]">
              Only {activeCount} healthy player{activeCount !== 1 ? "s" : ""}{" "}
              available — shortfall of {shortfall}
            </p>
          </div>
        )}

        <div className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10" />
                <TableHead className="w-10 text-center">#</TableHead>
                <TableHead>Player</TableHead>
                <TableHead>Position</TableHead>
                <TableHead className="w-20 text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {userRoster.map((player) => {
                const isAvailable = player.status === "active"
                const isSelected = selectedIds.has(player.id)
                const canSelect =
                  isAvailable && (isSelected || selectedIds.size < 11)

                return (
                  <TableRow
                    key={player.id}
                    className={cn(
                      isAvailable ? "cursor-pointer" : "opacity-50",
                      !isSelected &&
                        isAvailable &&
                        "hover:bg-muted/50 transition-colors",
                    )}
                    style={
                      isSelected
                        ? {
                            background: "oklch(0.22 0.04 145 / 0.15)",
                            boxShadow: "inset 3px 0 0 oklch(0.55 0.15 145)",
                          }
                        : undefined
                    }
                    onClick={() => {
                      if (canSelect) togglePlayer(player.id)
                    }}
                  >
                    <TableCell
                      className="text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {isAvailable ? (
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => {
                            if (canSelect) togglePlayer(player.id)
                          }}
                          disabled={!canSelect}
                          aria-label={`Select ${player.name}`}
                        />
                      ) : (
                        <Checkbox
                          disabled
                          checked={false}
                          aria-label={`${player.name} unavailable`}
                        />
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-center text-sm">
                      {player.number}
                    </TableCell>
                    <TableCell className="font-semibold">
                      <Link
                        href={leagueTeamPlayerPath(
                          leagueId,
                          userTeamId,
                          player.id,
                        )}
                        className="hover:text-accent transition-colors"
                      >
                        {player.name}
                      </Link>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {player.position}
                    </TableCell>
                    <TableCell className="text-center">
                      <PlayerStatusBadge
                        status={player.status}
                        injuryDescription={player.injury_description}
                      />
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>

        <div className="flex items-center justify-between border-t px-4 py-3">
          <p className="text-muted-foreground text-xs">
            {unavailableCount > 0 && (
              <>
                {unavailableCount} player{unavailableCount !== 1 ? "s" : ""}{" "}
                unavailable (
                {[
                  missNextCount > 0 ? "Miss Next Game" : null,
                  deadCount > 0 ? "Dead" : null,
                ]
                  .filter(Boolean)
                  .join(", ")}
                )
              </>
            )}
          </p>
          <Button
            onClick={handleConfirm}
            disabled={selectedCount === 0}
            size="sm"
          >
            Confirm Lineup
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
